import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  StrategyComment,
  COMMENT_ALLOWED_IMAGE_TYPES,
  COMMENT_MAX_FILE_SIZE,
  COMMENT_MAX_IMAGE_COUNT,
} from '../../strategy-catalog.models';
import { StrategyOptimizationService } from '../../services/strategy-optimization.service';
import { UserService } from '@app/services/user/user.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { getContenteEditableByBrowser } from '@utils/browser-utils';
import { IUserCollaboration } from '@pdqdev/pdq-shared/dist/interfaces';

@Component({
  selector: 'strategy-comments',
  standalone: false,
  templateUrl: './strategy-comments.component.html',
  styleUrl: './strategy-comments.component.scss',
})
export class StrategyCommentsComponent implements OnInit, OnChanges {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @ViewChild('commentTextarea') commentTextarea: ElementRef<HTMLElement>;

  @Input() strategyId: string;

  comments: StrategyComment[] = [];
  commentControl = new FormControl<string>('');
  imagePreviews: string[] = [];
  uploading = false;
  editingCommentId: number | null = null;
  editControl = new FormControl<string>('');
  usersList: IUserCollaboration[] = [];
  contenteditable: string;
  commentText = '';
  selectedMentions: IUserCollaboration[] = [];
  mentionOpen = false;
  private imageFiles: File[] = [];
  private userEmail = 'team@pdq.team';
  private destroyRef = inject(DestroyRef);

  constructor(
    private strategyOptimizationService: StrategyOptimizationService,
    private userService: UserService,
    private toastService: ToastService,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.contenteditable = getContenteEditableByBrowser();
    this.userService.currentPdqUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user?.email) {
        this.userEmail = user.email;
      }
    });
    this.userService.users$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
      if (users) {
        this.usersList = users.map((u) => ({
          id: u.email,
          img: u.profilePictureUrl,
          name: u.name,
        }));
      }
    });
    this.loadComments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['strategyId'] && !changes['strategyId'].firstChange) {
      this.loadComments();
    }
  }

  get textareaElement(): HTMLElement {
    return this.commentTextarea?.nativeElement;
  }

  get textareaValue(): string {
    return this.textareaElement?.textContent || '';
  }

  set textareaValue(value: string) {
    if (this.textareaElement) {
      this.renderer.setProperty(this.textareaElement, 'innerText', value);
    }
  }

  addComment(): void {
    const text = this.textareaValue.trim();
    if (!text && this.imageFiles.length === 0) return;

    this.uploading = true;
    const uploads$ =
      this.imageFiles.length > 0
        ? this.strategyOptimizationService.uploadCommentImages(this.imageFiles)
        : of([] as string[]);

    uploads$
      .pipe(
        switchMap((keys) =>
          this.strategyOptimizationService.addComment(
            this.strategyId,
            text,
            keys.length > 0 ? keys : undefined,
            this.validateMentions(this.selectedMentions, text),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.uploading = false;
          this.textareaValue = '';
          this.commentText = '';
          this.imagePreviews = [];
          this.imageFiles = [];
          this.selectedMentions = [];
          this.loadComments();
        },
        error: () => {
          this.uploading = false;
          this.toastService.showToast({ message: 'Failed to add comment', mode: 'danger' });
        },
      });
  }

  startEditComment(comment: StrategyComment): void {
    this.editingCommentId = comment.id;
    this.editControl.setValue(comment.text);
  }

  saveEditComment(): void {
    if (!this.editingCommentId) return;
    this.strategyOptimizationService
      .editComment(this.editingCommentId, (this.editControl.value || '').trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editingCommentId = null;
          this.editControl.setValue('');
          this.loadComments();
        },
        error: () => this.toastService.showToast({ message: 'Failed to edit comment', mode: 'danger' }),
      });
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editControl.setValue('');
  }

  onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEditComment();
    } else if (event.key === 'Escape') {
      this.cancelEditComment();
    }
  }

  deleteComment(commentId: number): void {
    this.strategyOptimizationService
      .deleteComment(commentId, this.strategyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadComments(),
        error: () => this.toastService.showToast({ message: 'Failed to delete comment', mode: 'danger' }),
      });
  }

  onCommentTextChanged(value: string): void {
    this.commentText = value;
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.mentionOpen) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.addComment();
    }
  }

  onMentionOpened(): void {
    this.mentionOpen = true;
  }

  onMentionClosed(): void {
    this.mentionOpen = false;
  }

  onUserMentionSelected(value: IUserCollaboration): void {
    if (!this.selectedMentions?.some((user) => user.id === value.id)) {
      this.selectedMentions.push(value);
    }
  }

  public insertSpanElement(userCollaboration: IUserCollaboration) {
    const el = document.createElement('a');
    el.contentEditable = 'false';
    el.className = 'user-mention';
    el.innerText = `@${userCollaboration.name}`;
    return el;
  }

  triggerFileUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!COMMENT_ALLOWED_IMAGE_TYPES.includes(file.type)) continue;
      if (file.size > COMMENT_MAX_FILE_SIZE) continue;
      if (this.imageFiles.length >= COMMENT_MAX_IMAGE_COUNT) break;

      this.imageFiles = [...this.imageFiles, file];
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreviews = [...this.imagePreviews, reader.result as string];
      };
      reader.readAsDataURL(file);
    }
    input.value = '';
  }

  removeImagePreview(index: number): void {
    this.imagePreviews = this.imagePreviews.filter((_, i) => i !== index);
    this.imageFiles = this.imageFiles.filter((_, i) => i !== index);
  }

  isOwnComment(comment: StrategyComment): boolean {
    return comment.author === this.userEmail;
  }

  getInitial(author?: string): string {
    return (author || 'T')[0].toUpperCase();
  }

  formatDate(isoString: string): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }

  private validateMentions(mentions: IUserCollaboration[], message: string): IUserCollaboration[] {
    if (!mentions?.length) {
      return [];
    }
    return mentions.flatMap((user) => (message.includes(user.name) ? { id: user.id, name: user.name } : []));
  }

  private loadComments(): void {
    this.strategyOptimizationService
      .getComments(this.strategyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((comments) => (this.comments = comments));
  }
}
